/**
 * SearchPage - Main unified search interface
 * TASK-270: Unified Search Interface
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search, Filter, History, Settings, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { SearchInput } from '../components/search/SearchInput';
import { StreamingResponse } from '../components/search/StreamingResponse';
import { useSearch } from '../hooks/useSearch';
import { clientService } from '../services/clientService';
import type { SourceType, SourceAttribution, SearchHistoryItem } from '../types/search.types';

// TASK-654: neutral example templates — never a client name that doesn't exist
// on this account. Real client names are substituted in once the roster loads.
const NEUTRAL_EXAMPLES = [
  'Show me portfolios with more than 1M in assets',
  'Find emails about market volatility',
  'Compare performance of tech holdings vs benchmarks',
];

const buildExamples = (clientNames: string[]): string[] => {
  const [first, second] = clientNames;
  return [
    ...(first ? [`What is ${first}'s risk profile?`] : []),
    ...(second ? [`Summarize recent activity for ${second}`] : []),
    ...NEUTRAL_EXAMPLES,
  ];
};

// Source filter options
const SOURCE_OPTIONS: { value: SourceType; label: string }[] = [
  { value: 'database', label: 'Database' },
  { value: 'email', label: 'Emails' },
  { value: 'market', label: 'Market Data' },
  { value: 'news', label: 'News' },
];

export function SearchPage() {
  const navigate = useNavigate();

  // Search state
  const [selectedSources, setSelectedSources] = useState<SourceType[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);

  // TASK-654: examples use THIS account's clients (neutral templates otherwise).
  const [exampleQueries, setExampleQueries] = useState<string[]>(NEUTRAL_EXAMPLES);
  useEffect(() => {
    let cancelled = false;
    clientService
      .fetchClients(0, 2)
      .then((res) => {
        const names = (res.clients ?? []).map((c) => c.name).filter(Boolean);
        if (!cancelled && names.length > 0) setExampleQueries(buildExamples(names));
      })
      .catch(() => undefined); // neutral templates stay
    return () => {
      cancelled = true;
    };
  }, []);

  // TASK-654: elapsed-time feedback + retry of the last query.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const lastQueryRef = useRef<string | null>(null);

  // TASK-482: the streaming WS error fires before `search`/`selectedSources` are in scope,
  // so reach them through refs to keep the one-shot REST fallback loop-safe.
  const restSearchRef = useRef<((query: string, sources?: SourceType[]) => void) | null>(null);
  const selectedSourcesRef = useRef<SourceType[]>([]);
  const pendingFallbackQuery = useRef<string | null>(null);

  // Use search hook
  const {
    isSearching,
    response,
    error,
    status,
    partialAnswer,
    streamingSources,
    search,
    searchStreaming,
    cancelSearch,
    reset: _reset,
  } = useSearch({
    onSearchComplete: (res) => {
      // Add to history
      const historyItem: SearchHistoryItem = {
        id: Date.now().toString(),
        query: res.query,
        timestamp: new Date().toISOString(),
        status: res.status,
        resultCount: res.sources.length,
      };
      setSearchHistory((prev) => [historyItem, ...prev.slice(0, 9)]);
    },
    onSearchError: () => {
      // TASK-482: if the WS stream fails, retry once over REST so search never dead-ends.
      const q = pendingFallbackQuery.current;
      pendingFallbackQuery.current = null; // cleared first → REST failure can't loop
      if (q && restSearchRef.current) {
        const sources = selectedSourcesRef.current;
        restSearchRef.current(q, sources.length > 0 ? sources : undefined);
      }
    },
  });
  restSearchRef.current = search;
  selectedSourcesRef.current = selectedSources;

  // Handle search — TASK-482: stream first (perceived-instant), REST as the fallback.
  const handleSearch = useCallback(
    (query: string) => {
      pendingFallbackQuery.current = query;
      lastQueryRef.current = query;
      searchStreaming(query, selectedSources.length > 0 ? selectedSources : undefined);
    },
    [searchStreaming, selectedSources]
  );

  // TASK-654: visible elapsed time while a search runs.
  useEffect(() => {
    if (!isSearching) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [isSearching]);

  const retryLast = useCallback(() => {
    if (lastQueryRef.current) handleSearch(lastQueryRef.current);
  }, [handleSearch]);

  // Handle source filter toggle
  const toggleSource = useCallback((source: SourceType) => {
    setSelectedSources((prev) =>
      prev.includes(source) ? prev.filter((s) => s !== source) : [...prev, source]
    );
  }, []);

  // Handle source click
  // TASK-605: a source that carries a link (KG facts → client DNA tab) navigates.
  const handleSourceClick = useCallback(
    (source: SourceAttribution) => {
      if (source.link?.startsWith('/')) {
        navigate(source.link);
      }
    },
    [navigate]
  );

  // Handle follow-up click
  const handleFollowUpClick = useCallback(
    (query: string) => {
      handleSearch(query);
    },
    [handleSearch]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Search className="h-6 w-6 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">Search</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  showFilters ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'
                )}
              >
                <Filter className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Search Input */}
        <SearchInput
          onSearch={handleSearch}
          isSearching={isSearching}
          suggestions={!response && !isSearching ? exampleQueries.slice(0, 3) : []}
          autoFocus
        />

        {/* TASK-654: staged progress while running — elapsed time, sources found
            so far, and Cancel. Partial results stream in below meanwhile. */}
        {isSearching && (
          <div
            data-testid="search-progress"
            className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>
              Searching{elapsedSeconds > 0 ? ` · ${elapsedSeconds}s` : ''}
              {streamingSources.length > 0
                ? ` · ${streamingSources.length} source${streamingSources.length === 1 ? '' : 's'} found so far`
                : ' · scanning your sources…'}
            </span>
            {elapsedSeconds >= 15 && (
              <span className="text-blue-700">
                Long-running search — partial results appear below as they arrive.
              </span>
            )}
            <button
              type="button"
              onClick={cancelSearch}
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
        )}

        {/* TASK-654: failed/timeout searches always offer a one-click retry. */}
        {!isSearching && (error || status === 'failed') && lastQueryRef.current && (
          <div className="mt-4">
            <button
              type="button"
              onClick={retryLast}
              data-testid="search-retry"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Retry “{lastQueryRef.current.slice(0, 60)}”
            </button>
          </div>
        )}

        {/* Source Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-white border rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter by source</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {SOURCE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => toggleSource(option.value)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-full border transition-all',
                    selectedSources.includes(option.value)
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  )}
                >
                  {option.label}
                </button>
              ))}
              {selectedSources.length > 0 && (
                <button
                  onClick={() => setSelectedSources([])}
                  className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search Results */}
        <div className="mt-6">
          <StreamingResponse
            response={response}
            partialAnswer={partialAnswer}
            streamingSources={streamingSources}
            status={status}
            isSearching={isSearching}
            error={error}
            onSourceClick={handleSourceClick}
            onFollowUpClick={handleFollowUpClick}
          />
        </div>

        {/* Empty State */}
        {!response && !isSearching && !error && (
          <div className="mt-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
              <Search className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Search across all your data
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Ask questions in natural language about your clients, portfolios, market data, and
              more. Try one of the example queries above to get started.
            </p>

            {/* Recent Searches */}
            {searchHistory.length > 0 && (
              <div className="mt-8 max-w-md mx-auto text-left">
                <div className="flex items-center gap-2 mb-3">
                  <History className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Recent searches</span>
                </div>
                <div className="space-y-2">
                  {searchHistory.slice(0, 5).map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSearch(item.query)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-600 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {item.query}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default SearchPage;
